var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState, useRef, useEffect } from 'react';
import { useGeocoding } from '../../_shared/components/arcgis-map/hooks/useGeocoding';
const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_MS = 300;
const DEFAULT_CENTER = [-77.0, 39.5];
const DEFAULT_ZOOM = 5;
const SELECTED_ZOOM = 17;
// Encapsulates address search state: debounced geocoding, suggestion list,
// selected address, and the map viewport derived from the selection.
export const usePropertySearch = () => {
    const [searchText, setSearchText] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [suggestionsFor, setSuggestionsFor] = useState('');
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
    const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);
    const geocoding = useGeocoding();
    const debounceRef = useRef(null);
    // The latest input, so a slow geocode for older text cannot land over a newer one's results.
    const latestInputRef = useRef('');
    useEffect(() => () => {
        if (debounceRef.current)
            clearTimeout(debounceRef.current);
    }, []);
    const onSearchInput = (value) => {
        setSearchText(value);
        setSelectedAddress(null);
        latestInputRef.current = value;
        if (debounceRef.current)
            clearTimeout(debounceRef.current);
        if (value.length < MIN_SEARCH_LENGTH) {
            setSuggestions([]);
            return;
        }
        debounceRef.current = setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
            const results = yield geocoding.searchAddress(value);
            if (latestInputRef.current !== value)
                return;
            setSuggestions(results);
            setSuggestionsFor(value.trim());
        }), DEBOUNCE_MS);
    };
    // The top geocode for a piece of text, undebounced.
    const resolveAddress = (value) => __awaiter(void 0, void 0, void 0, function* () {
        const results = yield geocoding.searchAddress(value.trim());
        return results.length > 0 ? results[0] : null;
    });
    const selectAddress = (candidate) => {
        setSelectedAddress(candidate);
        setSearchText(candidate.address);
        setSuggestions([]);
        setMapCenter([candidate.location[0], candidate.location[1]]);
        setMapZoom(SELECTED_ZOOM);
    };
    const clearSelection = () => {
        setSelectedAddress(null);
        setSearchText('');
        setSuggestions([]);
        setMapCenter(DEFAULT_CENTER);
        setMapZoom(DEFAULT_ZOOM);
    };
    return {
        searchText,
        onSearchInput,
        suggestions,
        suggestionsFor,
        resolveAddress,
        isLoading: geocoding.isLoading,
        selectedAddress,
        selectAddress,
        clearSelection,
        mapCenter,
        mapZoom,
    };
};
